import { useState, useEffect } from 'react';
import { initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, setDoc, getDoc } from "firebase/firestore";

const firebaseConfig = {
    apiKey: "AIzaSyDdoYtxRVi-J7eEDqrptUa9n1WFVuAuKJg",
    authDomain: "finance-os-81cd5.firebaseapp.com",
    projectId: "finance-os-81cd5",
    storageBucket: "finance-os-81cd5.firebasestorage.app",
    messagingSenderId: "654314106760",
    appId: "1:654314106760:web:46ffc410b5aa966816543d"
};

let app, auth, db;
try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
} catch (e) {
    console.log("Firebase init error (ignore if offline):", e);
}

const getExtendedDefault = (defaultData) => ({ 
    ...defaultData,
    trackerHistory: [],
    pendingBudgetAdjustment: 0,
    sinkingFunds: [], 
    additionalIncome: [],
    withdrawalRate: 4.0, 
    badges: [], 
    flags: { hasSimulatedDebt: false },
    scenario: { active: false, goals: [] },
    username: null,       
    elementalAvatar: 'air', 
    friends: [],
    readArticles: []
});

export const useAuthAndSync = (data, setData, setUser, setLoading, setIsSaving, DEFAULT_DATA_IMPORTED) => {

    const EXTENDED_DEFAULT = getExtendedDefault(DEFAULT_DATA_IMPORTED);

    // --- EFFECT 1: AUTH & LOAD (Smart Merge) ---
    useEffect(() => { 
        if(!auth || !db) return; 

        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => { 
            setUser(currentUser); 
            setLoading(true); 

            // 1. Get Local Data (Fast & Fresh)
            const localStr = window.localStorage.getItem('sf_data_bundle');
            const localData = localStr ? JSON.parse(localStr) : null;
            
            // Initialize with local data first to prevent UI flash
            if (localData) {
                 setData({ ...EXTENDED_DEFAULT, ...localData });
            }

            if (currentUser) { 
                const docRef = doc(db, "users", currentUser.uid); 
                const docSnap = await getDoc(docRef); 

                if (docSnap.exists()) {
                    const cloudData = docSnap.data();
                    let finalData = { ...EXTENDED_DEFAULT, ...cloudData };

                    // --- FIX: SMART MERGE CONFLICT RESOLUTION ---
                    // If Local Data exists, we compare it to Cloud to see if Local is "ahead".
                    
                    if (localData) {
                        // 1. Tour Check: If local says seen, but cloud says no, trust local.
                        if (localData.hasSeenTour && !cloudData.hasSeenTour) {
                            finalData.hasSeenTour = true;
                        }

                        // 2. History Check: If local has MORE history items, trust local (user just closed a period).
                        if ((localData.trackerHistory?.length || 0) > (cloudData.trackerHistory?.length || 0)) {
                            finalData.trackerHistory = localData.trackerHistory;
                            finalData.expenseItems = localData.expenseItems; // Likely changed during close
                            finalData.assets = localData.assets; // Likely changed during close
                            finalData.debts = localData.debts; // Likely changed during close
                            // If we trust local history, we trigger a save immediately below
                        }
                        
                        // 3. Offset Sync: Always prefer local UI state for navigation
                        finalData.trackerOffset = localData.trackerOffset || 0;
                        
                        // 4. Timestamp Check (The ultimate fallback)
                        // If local has a 'lastModified' and it's newer than cloud, trust local entirely
                        if (localData.lastModified && cloudData.lastModified) {
                            if (localData.lastModified > cloudData.lastModified) {
                                console.log("Local data is newer. Overriding cloud.");
                                finalData = { ...EXTENDED_DEFAULT, ...localData };
                            }
                        }
                    }
                    
                    // Schema migrations / Safety checks
                    finalData.badges = finalData.badges || [];
                    finalData.flags = finalData.flags || { hasSimulatedDebt: false };
                    finalData.friends = finalData.friends || [];
                    
                    // Login Streak Logic
                    const today = new Date().toISOString().split('T')[0];
                    if (finalData.lastLogin !== today) {
                        const last = finalData.lastLogin ? new Date(finalData.lastLogin) : new Date();
                        const diff = Math.floor((new Date() - last) / (1000 * 60 * 60 * 24));
                        if (diff === 1) finalData.loginStreak = (finalData.loginStreak || 0) + 1;
                        else if (diff > 1) finalData.loginStreak = 1; 
                        else if (!finalData.lastLogin) finalData.loginStreak = 1; 
                        finalData.lastLogin = today;
                        
                        // Force an immediate update to Cloud if streak changed
                        setDoc(docRef, finalData); 
                    }
                    
                    setData(finalData); 
                } else {
                    // No cloud data? Initialize.
                    if (localData) {
                        // If we have local data but no cloud data (first login?), push local to cloud
                        setDoc(docRef, localData);
                    } else {
                        setDoc(docRef, EXTENDED_DEFAULT);
                        setData(EXTENDED_DEFAULT);
                    }
                }
            } 
            setLoading(false); 
        }); 
        return () => unsubscribe(); 
    }, [setData, setUser, setLoading, DEFAULT_DATA_IMPORTED]); 

    // --- EFFECT 2: AUTO-SAVE & TIMESTAMP ---
    useEffect(() => { 
        if (!data) return;

        // 1. Save to Local Storage (Always)
        window.localStorage.setItem('sf_data_bundle', JSON.stringify(data)); 

        // 2. Save to Cloud (If logged in)
        if (auth && db && data && auth.currentUser) { 
            setIsSaving(true); 
            const saveToCloud = setTimeout(async () => { 
                try { 
                    // FIX: Inject Timestamp on Save
                    const dataWithTimestamp = { ...data, lastModified: Date.now() };
                    await setDoc(doc(db, "users", auth.currentUser.uid), dataWithTimestamp); 
                    setIsSaving(false); 
                } catch (e) { 
                    console.error("Cloud Save Error", e);
                    setIsSaving(false); 
                } 
            }, 2000); 
            
            return () => clearTimeout(saveToCloud); 
        } 
    }, [data, setIsSaving]); 
    
    return { auth, db };
};